import { AppProps } from "next/app";
import Head from "next/head";
import Router from "next/router";
import { useEffect } from "react";
import type { NextPage } from "next";

import { loadAdsLibrary } from "utils/ads";
import { withAuthContext } from "utils/auth";
import { loadDataLayer, getDataLayer } from "utils/analytics";
import { pauseAdsEffects } from "utils/ads";
import { VwoHideSnippet } from "utils/vwo";
import deferUntilLoad from "utils/deferUntilLoad";
import SiteMetadata from "components/SiteMetadata";

// Global, application-wide css imports
import "fonts.scss";
import "normalize.css";
import "app.scss";

deferUntilLoad(() => {
  loadAdsLibrary();
});

type WebVitalsMetric = {
  id: string;
  label: "web-vital" | "custom";
  name: string;
  startTime: number;
  value: number;
};

type NextPageWithDisableAnalytics = NextPage & {
  disableAnalytics?: boolean;
};

type AppPropsWithDisableAnalytics = AppProps & {
  Component: NextPageWithDisableAnalytics;
};

export const reportWebVitals = (metric: WebVitalsMetric) => {
  const dataLayer = getDataLayer<{
    event: "performanceMark";
    performance: WebVitalsMetric;
  }>();

  dataLayer.push({
    event: "performanceMark",
    performance: metric,
  });
};

const App = ({ Component, pageProps }: AppPropsWithDisableAnalytics) => {
  useEffect(() => {
    // Always stop the ads from running operations during routing
    Router.events.on("routeChangeStart", pauseAdsEffects);
    return () => {
      Router.events.off("routeChangeStart", pauseAdsEffects);
    };
  }, []);

  useEffect(() => {
    // page component can have an optional disableAnalytics property to bypass loading GTM
    if (Component.disableAnalytics) return;

    deferUntilLoad(() => {
      loadDataLayer();
    });
  }, [Component.disableAnalytics]);

  return (
    <>
      <SiteMetadata />
      <VwoHideSnippet />
      <Head>
        <link
          rel="preload"
          key="preload-ads"
          href="https://www.theatlantic.com/packages/adsjs/ads.min.js"
          as="script"
        />
      </Head>
      <Component {...pageProps} />
    </>
  );
};

export default withAuthContext(App);
